import React, { useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { formatColumnLabel } from '../utils/formatColumnLabel';

/**
 * Shared "Cols" dropdown: a toggle button showing "<label> (visible/total)",
 * a search box that filters the switch list as you type, and one switch per
 * toggleable column. Used by every page-level column-visibility dropdown
 * (AggregatedTickers, AccountDetails, AllAccounts, PortfolioDetails,
 * Transactions, Dashboard) and TickerTable's own built-in dropdown, so a
 * fix or improvement made here (e.g. the search box) applies everywhere.
 *
 * Each page keeps its own decisions about *which* columns are offered and in
 * what order (excluding 'symbol', pinning 'actions' next to 'strategy', etc.)
 * by pre-computing `columns` before passing it in -- this component only
 * owns the generic toggle-button/search/switch-list mechanics, plus whatever
 * page-specific footer content (hints, Reset/Set-as-Default buttons) is
 * passed as `children`.
 */
const ColumnVisibilityDropdown = ({
  visibleColumns,
  columns,
  onToggleColumn,
  disabledColumns = [],
  switchesDisabled = false,
  getLabel = formatColumnLabel,
  toggleLabel = 'Cols',
  idPrefix = 'column-toggle',
  searchPlaceholder = 'Search columns...',
  menuWidth = '250px',
  disabled = false,
  renderRowExtra,
  children
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matches = columns
    .map(column => ({ column, label: getLabel(column) }))
    .filter(({ label }) => label.toLowerCase().includes(normalizedSearch));

  return (
    <Dropdown
      className="d-inline-block"
      autoClose="outside"
      align="end"
      onToggle={(isOpen) => {
        if (!isOpen) setSearchTerm('');
      }}
    >
      <Dropdown.Toggle
        variant="outline-primary"
        size="sm"
        id="columns-dropdown"
        disabled={disabled}
        className="text-nowrap"
      >
        {toggleLabel} ({Object.values(visibleColumns).filter(Boolean).length}/{Object.keys(visibleColumns).length})
      </Dropdown.Toggle>
      <Dropdown.Menu
        style={{ minWidth: menuWidth }}
        className="p-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2">
          <strong>Toggle Column Visibility</strong>
        </div>
        <Form.Control
          type="text"
          size="sm"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          className="mb-2"
        />
        {matches.length === 0 ? (
          <div className="text-muted small mb-2">No columns match "{searchTerm}"</div>
        ) : (
          matches.map(({ column, label }) => {
            const isEnabled = !!visibleColumns[column];
            const isDisabled = switchesDisabled || disabledColumns.includes(column);
            const switchEl = (
              <Form.Check
                type="switch"
                id={`${idPrefix}-${column}`}
                label={label}
                checked={isEnabled}
                disabled={isDisabled}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleColumn(column, e.target.checked);
                }}
                onClick={(e) => e.stopPropagation()}
                className={renderRowExtra ? 'me-2 column-toggle-switch' : 'mb-1 column-toggle-switch'}
                style={{
                  opacity: isEnabled ? 1 : 0.55,
                  fontWeight: isEnabled ? 600 : 400
                }}
              />
            );

            if (!renderRowExtra) {
              return <React.Fragment key={column}>{switchEl}</React.Fragment>;
            }

            return (
              <div key={column} className="mb-1 d-flex justify-content-between align-items-center">
                {switchEl}
                {renderRowExtra(column)}
              </div>
            );
          })
        )}
        {children}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ColumnVisibilityDropdown;
